import type { BranchReceiptPrintConfig } from '../domain/printConfig';
import { isAllowedLocalAgentUrl } from '../../shared/localAgentUrl';

/**
 * Base de descargas del agente. El bucket `downloads` del proyecto de
 * producción (`mloaclsahrkijewdeqiv`) es intencionalmente prod-only: es la
 * única fuente pública del instalable y de `version.json` para todos los
 * entornos de la app. Override opcional vía `EXPO_PUBLIC_AGENT_DOWNLOADS_URL`
 * (staging/dev, tests locales) — sin él, todos los builds apuntan a prod.
 */
const AGENT_DOWNLOADS_BASE_URL = (
  process.env.EXPO_PUBLIC_AGENT_DOWNLOADS_URL ??
  'https://mloaclsahrkijewdeqiv.supabase.co/storage/v1/object/public/downloads/print-agent'
).replace(/\/$/, '');

/** Descarga del instalable del agente (bucket público Supabase — ver tools/vuelto-print-agent). */
export const PRINT_AGENT_DOWNLOAD_URL = `${AGENT_DOWNLOADS_BASE_URL}/vuelto-print-agent.zip`;

// #796: allowlist compartida con scale (mismo agente :9310) — localhost + LAN privada.
export const isAllowedPrintAgentUrl = isAllowedLocalAgentUrl;

export function resolvePrintAgentBase(config: Pick<BranchReceiptPrintConfig, 'agentUrl'>): string | null {
  const base = config.agentUrl.replace(/\/$/, '') || 'http://127.0.0.1:9310';
  return isAllowedPrintAgentUrl(base) ? base : null;
}

export type PrintAgentHealth = {
  ok: boolean;
  error?: string;
  version?: string;
  autostart?: boolean;
  /** v0.4.0+: task programada que reenciende el agente si muere. */
  watchdog?: boolean;
  /** v0.4.0+: segundos de vida del proceso. */
  uptimeSec?: number;
  /** v0.4.0+: ts del último job /print. */
  lastPrintAt?: number | null;
  /** v0.4.0+: resultado del último job. */
  lastPrintOk?: boolean | null;
  /** v0.4.0+: mensaje del último error de impresión. */
  lastError?: string | null;
};

export async function fetchPrintAgentHealth(
  config: Pick<BranchReceiptPrintConfig, 'agentUrl'>,
  signal?: AbortSignal,
): Promise<PrintAgentHealth> {
  const base = resolvePrintAgentBase(config);
  if (!base) {
    return { ok: false, error: 'URL del agente no permitida. Solo hosts locales o de red local.' };
  }
  try {
    // Timeout propio: un agente LAN apagado cuelga el connect ~1 min (timeout SO).
    const res = await fetch(`${base}/health`, {
      method: 'GET',
      signal: signal ?? AbortSignal.timeout(4000),
    });
    const data = (await res.json().catch(() => null)) as Record<string, unknown> | null;
    if (!res.ok) {
      return { ok: false, error: `Agente no disponible (${res.status}).` };
    }
    return {
      ok: true,
      version: typeof data?.version === 'string' ? data.version : undefined,
      autostart: data?.autostart === true,
      watchdog: data?.watchdog === true,
      uptimeSec: typeof data?.uptimeSec === 'number' ? data.uptimeSec : undefined,
      lastPrintAt: typeof data?.lastPrintAt === 'number' ? data.lastPrintAt : null,
      lastPrintOk: typeof data?.lastPrintOk === 'boolean' ? data.lastPrintOk : null,
      lastError: typeof data?.lastError === 'string' ? data.lastError : null,
    };
  } catch (err) {
    if ((err as { name?: string })?.name === 'AbortError') return { ok: false, error: 'cancelado' };
    return { ok: false, error: 'No se pudo hablar con el agente (¿está corriendo?).' };
  }
}

const VERSION_JSON_URL = `${AGENT_DOWNLOADS_BASE_URL}/version.json`;

/** Última versión publicada del agente (bucket público). null si no se pudo leer. */
export async function fetchLatestAgentVersion(signal?: AbortSignal): Promise<string | null> {
  try {
    const res = await fetch(VERSION_JSON_URL, {
      signal: signal ?? AbortSignal.timeout(5000),
      cache: 'no-store',
    });
    if (!res.ok) return null;
    const data = (await res.json().catch(() => null)) as { version?: unknown } | null;
    return typeof data?.version === 'string' ? data.version : null;
  } catch {
    return null;
  }
}

/** Comparación semver simple: 1 < si a<b, 0 igual, 1 si a>b. Raro/inválido → 0. */
export function compareAgentVersions(a: string, b: string): number {
  const pa = a.split('.').map((p) => parseInt(p, 10));
  const pb = b.split('.').map((p) => parseInt(p, 10));
  if (pa.some(Number.isNaN) || pb.some(Number.isNaN)) return 0;
  for (let i = 0; i < 3; i += 1) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d < 0 ? -1 : 1;
  }
  return 0;
}

export type AgentPrinter = { name: string; port: string; driver?: string };

export type PrintAgentPrintersResult =
  | { ok: true; printers: AgentPrinter[] }
  | { ok: false; error: string };

export async function fetchPrintAgentPrinters(
  config: Pick<BranchReceiptPrintConfig, 'agentUrl'>,
  signal?: AbortSignal,
): Promise<PrintAgentPrintersResult> {
  const base = resolvePrintAgentBase(config);
  if (!base) {
    return { ok: false, error: 'URL del agente no permitida.' };
  }
  try {
    const res = await fetch(`${base}/printers`, {
      method: 'GET',
      signal: signal ?? AbortSignal.timeout(4000),
    });
    const data = (await res.json().catch(() => null)) as
      | { printers?: Array<{ name?: string; port?: string; driver?: string }> }
      | null;
    if (!res.ok) return { ok: false, error: `Agente no disponible (${res.status}).` };
    const printers = (data?.printers ?? [])
      .filter((p): p is { name: string; port?: string; driver?: string } => typeof p?.name === 'string')
      .map((p) => ({ name: p.name, port: p.port ?? '', driver: p.driver }));
    return { ok: true, printers };
  } catch (err) {
    if ((err as { name?: string })?.name === 'AbortError') return { ok: false, error: 'cancelado' };
    return { ok: false, error: 'No se pudo hablar con el agente.' };
  }
}
